'use client';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  createPlayer,
  type LoadRequest,
  type PlayerInterface,
  type SmartclipPluginConfig,
} from '@foundation-player/api';
import { clsx } from 'clsx';
import { AppContext } from 'context/AppContext';
import { useGeo } from 'context/GeoContext';
import { useConsent } from 'hooks/useConsent';
import { useQueryExist } from 'hooks/useQueryExist';
import { useQueryParam } from 'hooks/useQueryParam';
import { getImageLoader } from 'utils/imageLoader';
import { geoAllowed } from 'utils/videoPlayer';
import { getFilename, normalizeBaseUrl } from '../../../utils/image';
import { FALLBACK_IMAGE_URL } from '../FallbackImage/FallbackImage';
import { Icon } from '../Icon';
import { createLoadRequest, geoBlockingErrorMessage } from './config';
import styles from './FoundationPlayer.module.scss';
import type { FoundationPlayerProps } from './types';

const FoundationPlayerClient = ({
  video,
  postRollDisabled,
  autoplay = false,
  startType = 0,
  isAdPlayingListener,
  adClipsLeftListener,
  currentAdTimeListener,
  contentFinishedListener,
  videoPlayingListener,
  showRelatedVideosListener,
  hasErrorListener,
  customPlayButton,
  contentType,
  clientIdentifier,
  embedType,
}: FoundationPlayerProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [showCustomPlayButton, setShowCustomPlayButton] = useState<boolean>(!!customPlayButton);
  const [sxpKeys, setSxpKeys] = useState<string | undefined>();
  const [moltenBundleSmartclipConfig, setMoltenBundleSmartclipConfig] = useState<
    SmartclipPluginConfig['smartclipConfig'] | undefined
  >();
  const [isMoltenReady, setIsMoltenReady] = useState<boolean>(false);

  const {
    analyticsData: { ivwCode },
    pageMetaData: { noVideoAds, adTags },
    env,
    version,
  } = useContext(AppContext);
  const {
    consent: { tcString },
    isConsentReady,
  } = useConsent();
  const userCountryCode = useGeo();
  const device = useQueryParam('device')?.toLowerCase();
  const autoplayUrl = useQueryExist('video.autoplay');
  const adsDisabled = useQueryExist('video.adsOff') || noVideoAds || video.fields['ads-free'] === 'true';
  const optimizedImg = video?.image?.baseUrl
    ? getImageLoader(
        normalizeBaseUrl(video.image.baseUrl),
        getFilename(video.fields.title, video.image.fields?.alt?.value),
        'c16_9'
      ).loader({
        src: normalizeBaseUrl(video?.image?.baseUrl),
        width: 1200,
      })
    : FALLBACK_IMAGE_URL;
  const posterUrl = video?.image?.isExtern ? normalizeBaseUrl(video.image.baseUrl) : optimizedImg;
  const playerRef = useRef<PlayerInterface | null>(null);
  const playerElementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMoltenBundle = () => {
      if (window?.MoltenBundle?.Video) {
        clearInterval(intervalId);
        const kw = (adTags ?? []).join(':');
        if (adTags) window.MoltenBundle.Video.setTargeting('kw', kw);

        const fetchMoltenData = async () => {
          try {
            const [sxpResult, smartclipResult] = await Promise.all([
              window.MoltenBundle.Video.getSXPKeysAsync(),
              window.MoltenBundle.Video.getSmartClientCoreAdvertisingConfig(),
            ]);
            setSxpKeys(sxpResult);
            setMoltenBundleSmartclipConfig(smartclipResult);
            setIsMoltenReady(true);
          } catch (error) {
            console.error('Error fetching MoltenBundle data:', error);
            setIsLoading(false);
          }
        };
        fetchMoltenData();
      }
    };

    const intervalId = setInterval(checkMoltenBundle, 100);
    checkMoltenBundle();

    return () => clearInterval(intervalId);
  }, [adTags]);

  const initPlayer = useCallback(async () => {
    if (playerRef.current) {
      try {
        await playerRef.current.destroy('recreate');
      } catch (error) {
        console.error('Error destroying player instance', error);
      }
      playerRef.current = null;
    }

    if (playerElementRef.current) {
      playerElementRef.current.innerHTML = '';
    }

    const loadRequest: LoadRequest = createLoadRequest({
      video,
      tcString,
      ivwCode,
      adsDisabled,
      env,
      postRollDisabled,
      sxpKeys,
      contentType,
      autoplay: autoplay || autoplayUrl,
      posterUrl,
      startType,
      version,
      device,
      clientIdentifier,
      moltenBundleSmartclipConfig,
      embedType,
    });

    try {
      const createdPlayer = await createPlayer({
        mountPoint: playerElementRef.current!,
        loadRequest,
        ui: { enabled: true },
        initialLoadingSpinnerOptions: { enabled: false },
      });

      playerRef.current = createdPlayer;

      createdPlayer.state.onValueChange('currentTimeInSeconds', ({ currentTimeInSeconds }) => {
        if (typeof currentTimeInSeconds === 'number') {
          currentAdTimeListener?.(currentTimeInSeconds);
        }
      });

      createdPlayer.state.onValueChange('linearAdActive', ({ linearAdActive }) => {
        isAdPlayingListener?.(!!linearAdActive);
      });

      createdPlayer
        .getAdvertisingPlugin()
        ?.state.onValueChange('numberOfStartedAdClips', ({ numberOfStartedAdClips, totalNumberOfAdClips }) => {
          adClipsLeftListener?.(totalNumberOfAdClips - (numberOfStartedAdClips - 1));
        });

      let lastState: string | undefined;
      createdPlayer.state.onValueChange('state', ({ state }) => {
        if (state !== lastState) {
          videoPlayingListener?.(state === 'playing');
          lastState = state;
        }

        if (state === 'ended') {
          showRelatedVideosListener?.(true);
          contentFinishedListener?.(true);
        }
      });

      if (customPlayButton) {
        setShowCustomPlayButton(true);
      }
    } catch (error) {
      hasErrorListener?.(true);
      console.error('Error creating player instance', error);
      setIsLoading(false);
    }

    setIsLoading(false);
  }, [
    video,
    tcString,
    ivwCode,
    adsDisabled,
    env,
    postRollDisabled,
    sxpKeys,
    contentType,
    autoplay,
    autoplayUrl,
    posterUrl,
    startType,
    version,
    device,
    clientIdentifier,
    moltenBundleSmartclipConfig,
    embedType,
    currentAdTimeListener,
    isAdPlayingListener,
    adClipsLeftListener,
    videoPlayingListener,
    showRelatedVideosListener,
    contentFinishedListener,
    hasErrorListener,
    customPlayButton,
  ]);

  const handleCustomPlayButtonClick = () => {
    setShowCustomPlayButton(false);
    playerRef.current?.play();
  };

  useEffect(() => {
    if (isConsentReady && isMoltenReady && geoAllowed(video?.fields?.['regions-allowed'], userCountryCode) && video) {
      (async () => {
        await initPlayer();
      })();
    }
  }, [isConsentReady, isMoltenReady, video, initPlayer, userCountryCode]);

  useEffect(() => {
    if (userCountryCode && !geoAllowed(video?.fields?.['regions-allowed'], userCountryCode)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowErrorMessage(true);
    }
  }, [userCountryCode, video?.fields]);

  return (
    <div
      className={clsx(styles.clientWrapper, showCustomPlayButton && styles.showCustomPlayButton)}
      style={{ aspectRatio: '16 / 9' }}
    >
      <div ref={playerElementRef} className={styles.playerElement} />
      {isLoading && <span className={styles.loading}>Video lädt...</span>}
      {showErrorMessage && <div className={styles.errorMessage}>{geoBlockingErrorMessage}</div>}
      {showCustomPlayButton && !isLoading && (
        <button className={styles.customPlayButton} onClick={handleCustomPlayButtonClick} aria-label="Play">
          <Icon name="playOutline" className={styles.icon} size={23} />
          <span className={styles.startVideo}>Video starten</span>
        </button>
      )}
    </div>
  );
};

export default FoundationPlayerClient;
